import * as React from "react";
import { getLanguageFlagSrc } from "../../shared/utility/designer-languages";
import "./banner-illustrations.scss";

/**
 * The eight miniatures behind the auth banner's slider. Each one is a shrunk-down
 * portrait of a real designer tool, drawn from that tool's signature elements — the
 * canvas selection chrome, the cron cell, the Support switch — so a visitor recognises
 * the product rather than a stock illustration.
 *
 * Two rules hold for every component in this file:
 *
 * - **Nothing focusable.** The viewport that hosts these is `aria-hidden`, and a
 *   focusable node under `aria-hidden` is reachable by Tab but invisible to a screen
 *   reader. No button, link, input or `tabIndex` belongs here.
 * - **No `backdrop-filter`.** `__glass` above already blurs the shader once; a frosted
 *   mini-panel in here would blur the glass's own output, and every one of them would
 *   re-rasterise on every shader frame. Flat translucent fills only.
 *
 * They live in one file because they share a single vocabulary (surface, tint, skeleton
 * bar, chip) and one stylesheet; splitting eight small drawings across eight files would
 * buy nothing but imports.
 */

const BLOCK: string = "kuika-banner-mock";

interface BannerMockFrameProps {
  tool: string;
  children: React.ReactNode;
}

/** The shared shell: a bordered surface with a soft accent wash in one corner. */
const BannerMockFrame: React.FC<BannerMockFrameProps> = ({ tool, children }) => {
  return (
    <div className={`${BLOCK} ${BLOCK}--${tool}`}>
      <span className={`${BLOCK}__tint`} />
      {children}
    </div>
  );
};

export const DragAndDropIllustration: React.FC = () => {
  return (
    <BannerMockFrame tool="drag-and-drop">
      <div className={`${BLOCK}__dd-body`}>
        <div className={`${BLOCK}__dd-rail`}>
          <span className={`${BLOCK}__eyebrow`}>Elements</span>

          <div className={`${BLOCK}__dd-grid`}>
            <div className={`${BLOCK}__dd-card`}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <rect x="3" y="6" width="18" height="12" rx="2" stroke="currentColor" strokeWidth="1.6" />
                <path d="M3 11H21" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
              </svg>
              <span className={`${BLOCK}__dd-card-name`}>Row</span>
            </div>

            <div className={`${BLOCK}__dd-card`}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M5 8H19M5 13H15M5 17H12" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
              </svg>
              <span className={`${BLOCK}__dd-card-name`}>Label</span>
            </div>

            <div className={`${BLOCK}__dd-card`}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <rect x="3" y="8" width="18" height="8" rx="4" stroke="currentColor" strokeWidth="1.6" />
              </svg>
              <span className={`${BLOCK}__dd-card-name`}>Button</span>
            </div>

            <div className={`${BLOCK}__dd-card ${BLOCK}__dd-card--source`}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <rect x="3" y="4" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="1.6" />
                <path d="M3 9H21M3 14H21M9 9V20" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
              </svg>
              <span className={`${BLOCK}__dd-card-name`}>Table</span>
            </div>
          </div>
        </div>

        <div className={`${BLOCK}__dd-canvas`}>
          <div className={`${BLOCK}__dd-block`}>
            <span className={`${BLOCK}__bar ${BLOCK}__bar--strong`} style={{ width: "46%", height: "7px" }} />
            <span className={`${BLOCK}__bar`} style={{ width: "100%", height: "5px" }} />
          </div>

          <span className={`${BLOCK}__dd-drop`} />

          {/* What the drop leaves behind is a Table, so it has to read as one: a header
              strip over ruled rows with column gutters, not a stack of text lines. */}
          <div className={`${BLOCK}__dd-placed`}>
            <div className={`${BLOCK}__dd-tbl-row ${BLOCK}__dd-tbl-row--head`}>
              <span className={`${BLOCK}__dd-tbl-cell`} style={{ width: "42%" }} />
              <span className={`${BLOCK}__dd-tbl-cell`} style={{ width: "30%" }} />
              <span className={`${BLOCK}__dd-tbl-cell`} style={{ width: "20%" }} />
            </div>
            <div className={`${BLOCK}__dd-tbl-row`}>
              <span className={`${BLOCK}__dd-tbl-cell`} style={{ width: "42%" }} />
              <span className={`${BLOCK}__dd-tbl-cell`} style={{ width: "30%" }} />
              <span className={`${BLOCK}__dd-tbl-cell`} style={{ width: "20%" }} />
            </div>
            <div className={`${BLOCK}__dd-tbl-row`}>
              <span className={`${BLOCK}__dd-tbl-cell`} style={{ width: "42%" }} />
              <span className={`${BLOCK}__dd-tbl-cell`} style={{ width: "30%" }} />
              <span className={`${BLOCK}__dd-tbl-cell`} style={{ width: "20%" }} />
            </div>
          </div>
        </div>

        {/* The ghost and the pointer travel as one unit, rail card to canvas and back. */}
        <div className={`${BLOCK}__dd-drag`}>
          <div className={`${BLOCK}__dd-ghost`}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <rect x="3" y="4" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="1.7" />
              <path d="M3 9H21M3 14H21M9 9V20" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
            </svg>
            <span className={`${BLOCK}__dd-ghost-name`}>Table</span>
          </div>

          <span className={`${BLOCK}__dd-pointer`}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path
                d="M5 3L18.5 11.4L12.4 12.6L9.6 18.4L5 3Z"
                fill="currentColor"
                stroke="#04081a"
                strokeWidth="1.2"
                strokeLinejoin="round"
              />
            </svg>
          </span>
        </div>
      </div>
    </BannerMockFrame>
  );
};

export const DatasourcesIllustration: React.FC = () => {
  return (
    <BannerMockFrame tool="datasources">
      <div className={`${BLOCK}__ds-body`}>
        <div className={`${BLOCK}__ds-connectors`}>
          <span className={`${BLOCK}__chip ${BLOCK}__chip--live`}>
            <span className={`${BLOCK}__chip-dot`} />
            MS SQL Server
          </span>
          <span className={`${BLOCK}__chip`}>PostgreSQL</span>
          <span className={`${BLOCK}__chip`}>REST API</span>
          <span className={`${BLOCK}__chip`}>SAP</span>
          <span className={`${BLOCK}__chip`}>OData</span>
        </div>

        <div className={`${BLOCK}__ds-diagram`}>
          <div className={`${BLOCK}__ds-table`}>
            <span className={`${BLOCK}__ds-accent`} />
            <div className={`${BLOCK}__ds-head`}>
              <span className={`${BLOCK}__ds-schema`}>dbo</span>
              <span className={`${BLOCK}__ds-name`}>Customers</span>
              <span className={`${BLOCK}__ds-count`}>6</span>
            </div>

            <div className={`${BLOCK}__ds-col`}>
              <span className={`${BLOCK}__ds-key ${BLOCK}__ds-key--on`}>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <circle cx="8" cy="12" r="4" stroke="currentColor" strokeWidth="2" />
                  <path d="M12 12H21M18 12V15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </span>
              <span className={`${BLOCK}__ds-col-name`}>Id</span>
              <span className={`${BLOCK}__ds-type`}>int</span>
            </div>

            <div className={`${BLOCK}__ds-col`}>
              <span className={`${BLOCK}__ds-key`} />
              <span className={`${BLOCK}__ds-col-name`}>CompanyName</span>
              <span className={`${BLOCK}__ds-type`}>text</span>
            </div>

            <div className={`${BLOCK}__ds-col`}>
              <span className={`${BLOCK}__ds-key`} />
              <span className={`${BLOCK}__ds-col-name`}>Country</span>
              <span className={`${BLOCK}__ds-type`}>text</span>
            </div>

            <div className={`${BLOCK}__ds-col`}>
              <span className={`${BLOCK}__ds-key`} />
              <span className={`${BLOCK}__ds-col-name`}>CreatedAt</span>
              <span className={`${BLOCK}__ds-type`}>date</span>
            </div>
          </div>

          {/* The one saturated mark in the panel: the relation between the two tables. */}
          <span className={`${BLOCK}__ds-link`}>
            <svg width="44" height="110" viewBox="0 0 44 110" fill="none" aria-hidden="true">
              <path d="M0 34H20V76H44" stroke="currentColor" strokeWidth="1.4" />
              <circle cx="2.5" cy="34" r="2.5" fill="currentColor" />
              <circle cx="41.5" cy="76" r="2.5" fill="currentColor" />
            </svg>
          </span>

          <div className={`${BLOCK}__ds-table`}>
            <span className={`${BLOCK}__ds-accent`} />
            <div className={`${BLOCK}__ds-head`}>
              <span className={`${BLOCK}__ds-schema`}>dbo</span>
              <span className={`${BLOCK}__ds-name`}>Orders</span>
              <span className={`${BLOCK}__ds-count`}>9</span>
            </div>

            <div className={`${BLOCK}__ds-col`}>
              <span className={`${BLOCK}__ds-key`} />
              <span className={`${BLOCK}__ds-col-name`}>Id</span>
              <span className={`${BLOCK}__ds-type`}>int</span>
            </div>

            <div className={`${BLOCK}__ds-col`}>
              <span className={`${BLOCK}__ds-key`} />
              <span className={`${BLOCK}__ds-col-name`}>Total</span>
              <span className={`${BLOCK}__ds-type`}>money</span>
            </div>

            <div className={`${BLOCK}__ds-col`}>
              <span className={`${BLOCK}__ds-key ${BLOCK}__ds-key--on`}>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path
                    d="M4 12H13M4 12L7 9M4 12L7 15"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <circle cx="17" cy="12" r="4" stroke="currentColor" strokeWidth="2" />
                </svg>
              </span>
              <span className={`${BLOCK}__ds-col-name`}>CustomerId</span>
              <span className={`${BLOCK}__ds-type`}>int</span>
            </div>

            <div className={`${BLOCK}__ds-col`}>
              <span className={`${BLOCK}__ds-key`} />
              <span className={`${BLOCK}__ds-col-name`}>Status</span>
              <span className={`${BLOCK}__ds-type`}>text</span>
            </div>
          </div>
        </div>
      </div>
    </BannerMockFrame>
  );
};

export const ProcessAutomationIllustration: React.FC = () => {
  return (
    <BannerMockFrame tool="process-automation">
      <div className={`${BLOCK}__wf-body`}>
        <div className={`${BLOCK}__wf-head`}>
          <span className={`${BLOCK}__wf-dot`} />
          <span>Order approval</span>
        </div>

        <div className={`${BLOCK}__wf-canvas`}>
          {/* Steel outlines on neutral bodies with one accented node — the relationship
              the real diagram draws with --dark-6 on --dark-0, moved onto a dark ground.
              Sequence flows carry no labels, because the editor does not put any there. */}
          <svg width="100%" viewBox="0 0 468 168" fill="none" aria-hidden="true">
            <path className={`${BLOCK}__wf-flow`} d="M40 58H58" strokeWidth="1.4" />
            <path className={`${BLOCK}__wf-arrow`} d="M56 54L62 58L56 62Z" />

            <path className={`${BLOCK}__wf-flow`} d="M192 58H212" strokeWidth="1.4" />
            <path className={`${BLOCK}__wf-arrow`} d="M210 54L216 58L210 62Z" />

            <path className={`${BLOCK}__wf-flow`} d="M262 58H292" strokeWidth="1.4" />
            <path className={`${BLOCK}__wf-arrow`} d="M290 54L296 58L290 62Z" />

            <path className={`${BLOCK}__wf-flow`} d="M240 80V128H292" strokeWidth="1.4" />
            <path className={`${BLOCK}__wf-arrow`} d="M290 124L296 128L290 132Z" />

            <circle className={`${BLOCK}__wf-shape`} cx="26" cy="58" r="12" strokeWidth="1.4" />

            <rect className={`${BLOCK}__wf-active`} x="62" y="24" width="130" height="68" rx="8" strokeWidth="1.5" />
            <g className={`${BLOCK}__wf-active-glyph`} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="77" cy="37" r="3.6" />
              <path d="M71 47C71 44 73.6 42.6 77 42.6C80.4 42.6 83 44 83 47" />
            </g>
            <text className={`${BLOCK}__wf-label`} x="127" y="66" textAnchor="middle">
              Approve
            </text>

            <path className={`${BLOCK}__wf-shape`} d="M240 36L262 58L240 80L218 58Z" strokeWidth="1.4" />
            <path
              className={`${BLOCK}__wf-glyph`}
              d="M234 52L246 64M246 52L234 64"
              strokeWidth="1.5"
              strokeLinecap="round"
            />

            <circle className={`${BLOCK}__wf-shape`} cx="308" cy="58" r="12" strokeWidth="3" />

            <rect className={`${BLOCK}__wf-shape`} x="306" y="106" width="130" height="44" rx="8" strokeWidth="1.4" />
            <text className={`${BLOCK}__wf-label ${BLOCK}__wf-label--muted`} x="371" y="132" textAnchor="middle">
              Notify
            </text>
          </svg>
        </div>
      </div>
    </BannerMockFrame>
  );
};

/** The one check mark, drawn once and reused down the activity list. */
const AiCheckGlyph: React.FC = () => {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M5 12.5L10 17.5L19 7"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

/**
 * A spinner-and-checkmarks list is what every AI product shows and says nothing about
 * this one, so the panel leads with what is actually particular to Kuika's orchestrator:
 * a real prompt, the Execution Plan it produced, and the named specialists each step is
 * handed to. Every string here is one the product emits — the plan header and its step
 * counter, the `Designer` / `Binder` / `Wirer` agent labels, and the tool-activity line
 * with its "+N more" component roll-up.
 */
export const AiIllustration: React.FC = () => {
  return (
    <BannerMockFrame tool="ai">
      <div className={`${BLOCK}__ai-body`}>
        <div className={`${BLOCK}__ai-ask`}>
          <span className={`${BLOCK}__ai-bubble`}>Add an orders table with a status filter</span>
        </div>

        <div className={`${BLOCK}__ai-plan`}>
          <div className={`${BLOCK}__ai-plan-head`}>
            <span className={`${BLOCK}__ai-plan-mark`}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                  d="M9 3.5C9 6.54 6.54 9 3.5 9C6.54 9 9 11.46 9 14.5C9 11.46 11.46 9 14.5 9C11.46 9 9 6.54 9 3.5Z"
                  stroke="currentColor"
                  strokeWidth="1.7"
                  strokeLinejoin="round"
                />
                <path
                  d="M16.5 12.5C16.5 14.16 15.16 15.5 13.5 15.5C15.16 15.5 16.5 16.84 16.5 18.5C16.5 16.84 17.84 15.5 19.5 15.5C17.84 15.5 16.5 14.16 16.5 12.5Z"
                  stroke="currentColor"
                  strokeWidth="1.7"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
            <span className={`${BLOCK}__ai-plan-title`}>Execution Plan</span>
            <span className={`${BLOCK}__ai-plan-count`}>2/3</span>
          </div>

          <div className={`${BLOCK}__ai-step`}>
            <span className={`${BLOCK}__ai-step-mark ${BLOCK}__ai-step-mark--done`}>
              <AiCheckGlyph />
            </span>
            <span className={`${BLOCK}__ai-step-text`}>Add table and filter panel</span>
            <span className={`${BLOCK}__ai-step-agent`}>Designer</span>
          </div>

          <div className={`${BLOCK}__ai-step`}>
            <span className={`${BLOCK}__ai-step-mark ${BLOCK}__ai-step-mark--done`}>
              <AiCheckGlyph />
            </span>
            <span className={`${BLOCK}__ai-step-text`}>Bind columns to Orders</span>
            <span className={`${BLOCK}__ai-step-agent`}>Binder</span>
          </div>

          <div className={`${BLOCK}__ai-step ${BLOCK}__ai-step--live`}>
            <span className={`${BLOCK}__ai-step-mark ${BLOCK}__ai-step-mark--live`}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <circle
                  cx="12"
                  cy="12"
                  r="9"
                  stroke="currentColor"
                  strokeWidth="2.4"
                  strokeLinecap="round"
                  strokeDasharray="24 10"
                />
              </svg>
            </span>
            <span className={`${BLOCK}__ai-step-text`}>Wire the filter action</span>
            <span className={`${BLOCK}__ai-step-agent`}>Wirer</span>
          </div>
        </div>

        <div className={`${BLOCK}__ai-activity`}>
          <span className={`${BLOCK}__ai-pulse`} />
          <span className={`${BLOCK}__ai-activity-text`}>Configuring OrdersTable, StatusFilter +2 more</span>
        </div>
      </div>
    </BannerMockFrame>
  );
};

/**
 * The four flags the Email Builder miniature shows, as locale codes rather than colours:
 * the real assets come from the same catalog the Languages panel and the template
 * language control read, so a flag redraw lands here too. Hand-rolled CSS stripes read as
 * a bug at 17px — the Turkish crescent and the US canton simply cannot be gradients.
 */
const EMAIL_BUILDER_LOCALES: string[] = ["tr_TR", "en_US", "de_DE", "fr_FR"];

export const EmailBuilderIllustration: React.FC = () => {
  return (
    <BannerMockFrame tool="email-builder">
      <div className={`${BLOCK}__em-body`}>
        {/* A light sheet, because that is what an email is. */}
        <div className={`${BLOCK}__em-paper`}>
          <div className={`${BLOCK}__em-brand`}>
            <svg width="16" height="16" viewBox="0 0 14 14" fill="none" aria-hidden="true">
              <path
                d="M0 7C0 3.70017 0 2.05025 1.02513 1.02513C2.05025 0 3.70017 0 7 0C10.2998 0 11.9497 0 12.9749 1.02513C14 2.05025 14 3.70017 14 7C14 10.2998 14 11.9497 12.9749 12.9749C11.9497 14 10.2998 14 7 14C3.70017 14 2.05025 14 1.02513 12.9749C0 11.9497 0 10.2998 0 7Z"
                fill="#246DFF"
              />
              <path
                d="M6.42662 11.2C6.17634 11.2 5.90193 11.1174 5.60943 10.9582C4.26153 10.2208 3.2604 9.09769 2.77189 8.29289C2.5005 7.84611 2.3437 7.5095 2.33465 7.10862V7.0719C2.33465 7.05048 2.33164 7.026 2.33465 7.00458C2.33465 6.97398 2.33465 6.9495 2.33465 6.92808V6.89135C2.34671 6.48742 2.50352 6.15081 2.77491 5.70709C3.26341 4.90228 4.26454 3.77922 5.61245 3.04174C5.90495 2.88261 6.17935 2.79999 6.42963 2.79999C6.67992 2.79999 6.84275 2.87955 6.93924 2.92545L6.95432 2.93157C7.25888 3.0754 7.46393 3.28043 7.56043 3.54665L7.56947 3.56808C7.60264 3.6507 7.68406 3.84655 7.56344 4.13726C7.46092 4.38206 7.1654 4.66053 7.0086 4.80742C6.97241 4.84108 6.94226 4.86862 6.92417 4.89004C6.36329 5.46228 5.66371 6.17529 5.67276 7.00152C5.66069 7.82469 6.36028 8.53769 6.92417 9.10993C6.94528 9.13135 6.97241 9.1589 7.0086 9.19256C7.16842 9.3425 7.46092 9.61791 7.56344 9.86272C7.68406 10.1534 7.60566 10.3493 7.56947 10.4319L7.56043 10.4533C7.46092 10.7165 7.25888 10.9246 6.95432 11.0684L6.93924 11.0745C6.83974 11.1204 6.67388 11.2 6.42963 11.2H6.42662Z"
                fill="#ffffff"
              />
              <path
                d="M9.58515 8.94242C9.3752 9.06907 9.2106 9.12045 9.09371 9.1248C8.73186 9.04127 8.35931 8.82334 8.02533 8.53975C7.68321 8.24924 7.42701 7.92847 7.30348 7.71776C6.87897 6.98984 7.23502 6.37799 7.2666 6.32372L7.27786 6.30744L7.28696 6.29359C7.3287 6.23602 7.59421 5.86883 7.96042 5.50422C8.16141 5.30411 8.37733 5.12037 8.5884 4.98997C8.8057 4.85573 8.97008 4.80766 9.08025 4.80766L9.09053 4.80879C9.39333 4.84806 9.74378 5.03965 10.0894 5.34435C10.4277 5.64254 10.7031 5.99803 10.8572 6.26121C11.2888 6.99725 10.9206 7.61976 10.8825 7.68062C10.8085 7.77939 10.5568 8.10977 10.2109 8.44407C10.0113 8.63699 9.79628 8.81505 9.58515 8.94242Z"
                fill="#ffffff"
                stroke="#ffffff"
                strokeWidth="1.2"
              />
            </svg>
            <span className={`${BLOCK}__em-word`}>Kuika</span>
          </div>

          <div className={`${BLOCK}__em-content`}>
            <span className={`${BLOCK}__em-h`} />
            <span className={`${BLOCK}__em-line`} style={{ width: "100%" }} />
            <span className={`${BLOCK}__em-line`} style={{ width: "92%" }} />
            <span className={`${BLOCK}__em-line`} style={{ width: "64%" }} />

            {/* Unlayer marks the selected block; Kuika overrides its accent to the brand
                blue, so this outline is the product's own colour. */}
            <div className={`${BLOCK}__em-selection`}>
              <span className={`${BLOCK}__em-selection-tag`}>Button</span>
              <span className={`${BLOCK}__em-cta`}>Verify email</span>
            </div>

            <span className={`${BLOCK}__em-rule`} />

            <div className={`${BLOCK}__em-foot`}>
              <span className={`${BLOCK}__em-foot-line`} style={{ width: "78%" }} />
              <span className={`${BLOCK}__em-foot-line`} style={{ width: "52%" }} />
            </div>
          </div>
        </div>

        <div className={`${BLOCK}__em-side`}>
          <div className={`${BLOCK}__em-tabs`}>
            <span className={`${BLOCK}__em-tab ${BLOCK}__em-tab--on`}>Content</span>
            <span className={`${BLOCK}__em-tab`}>Rows</span>
            <span className={`${BLOCK}__em-tab`}>Settings</span>
          </div>

          <div className={`${BLOCK}__em-blocks`}>
            <div className={`${BLOCK}__em-block`}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M5 6H19M9 6V18" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
              </svg>
              <span className={`${BLOCK}__em-block-name`}>Title</span>
            </div>

            <div className={`${BLOCK}__em-block`}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M5 7H19M5 12H19M5 17H13" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
              </svg>
              <span className={`${BLOCK}__em-block-name`}>Text</span>
            </div>

            <div className={`${BLOCK}__em-block`}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <rect x="3" y="8" width="18" height="8" rx="4" stroke="currentColor" strokeWidth="1.7" />
              </svg>
              <span className={`${BLOCK}__em-block-name`}>Button</span>
            </div>

            <div className={`${BLOCK}__em-block`}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="1.7" />
                <path
                  d="M3.5 16L9 11L14 15L17 12.5L20.5 15.5"
                  stroke="currentColor"
                  strokeWidth="1.7"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span className={`${BLOCK}__em-block-name`}>Image</span>
            </div>

            <div className={`${BLOCK}__em-block`}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M4 12H20" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
              </svg>
              <span className={`${BLOCK}__em-block-name`}>Divider</span>
            </div>

            <div className={`${BLOCK}__em-block`}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <circle cx="7" cy="12" r="2.6" stroke="currentColor" strokeWidth="1.7" />
                <circle cx="17" cy="7" r="2.6" stroke="currentColor" strokeWidth="1.7" />
                <circle cx="17" cy="17" r="2.6" stroke="currentColor" strokeWidth="1.7" />
                <path
                  d="M9.4 10.9L14.6 8.2M9.4 13.1L14.6 15.8"
                  stroke="currentColor"
                  strokeWidth="1.7"
                  strokeLinecap="round"
                />
              </svg>
              <span className={`${BLOCK}__em-block-name`}>Social</span>
            </div>
          </div>

          <div className={`${BLOCK}__em-langs`}>
            <span className={`${BLOCK}__em-flags`}>
              {EMAIL_BUILDER_LOCALES.map((locale: string): React.ReactNode => (
                <img
                  key={locale}
                  className={`${BLOCK}__em-flag`}
                  src={getLanguageFlagSrc(locale)}
                  alt=""
                  aria-hidden="true"
                />
              ))}
              <span className={`${BLOCK}__em-more`}>+2</span>
            </span>
            <span className={`${BLOCK}__em-langs-note`}>6 languages</span>
          </div>
        </div>
      </div>
    </BannerMockFrame>
  );
};

export const ReportDesignerIllustration: React.FC = () => {
  return (
    <BannerMockFrame tool="report-designer">
      <span className={`${BLOCK}__rp-ruler ${BLOCK}__rp-ruler--top`} />
      <span className={`${BLOCK}__rp-ruler ${BLOCK}__rp-ruler--left`} />

      <div className={`${BLOCK}__rp-stage`}>
        {/* Cropped on purpose: a whole A4 shrunk into 230px leaves nothing legible. */}
        <div className={`${BLOCK}__rp-page`}>
          <span className={`${BLOCK}__rp-margin`} />

          <div className={`${BLOCK}__rp-content`}>
            <span className={`${BLOCK}__rp-heading`} />
            <span className={`${BLOCK}__rp-sub`} />

            <div className={`${BLOCK}__rp-split`}>
              <div className={`${BLOCK}__rp-table`}>
                <div className={`${BLOCK}__rp-row`}>
                  <span className={`${BLOCK}__rp-cell ${BLOCK}__rp-cell--head`} style={{ width: "44%" }} />
                  <span className={`${BLOCK}__rp-cell ${BLOCK}__rp-cell--head`} style={{ width: "26%" }} />
                  <span className={`${BLOCK}__rp-cell ${BLOCK}__rp-cell--head`} style={{ width: "20%" }} />
                </div>
                <div className={`${BLOCK}__rp-row`}>
                  <span className={`${BLOCK}__rp-cell`} style={{ width: "44%" }} />
                  <span className={`${BLOCK}__rp-cell`} style={{ width: "26%" }} />
                  <span className={`${BLOCK}__rp-cell`} style={{ width: "20%" }} />
                </div>
                <div className={`${BLOCK}__rp-row`}>
                  <span className={`${BLOCK}__rp-cell`} style={{ width: "44%" }} />
                  <span className={`${BLOCK}__rp-cell`} style={{ width: "26%" }} />
                  <span className={`${BLOCK}__rp-cell`} style={{ width: "20%" }} />
                </div>
                <div className={`${BLOCK}__rp-row`}>
                  <span className={`${BLOCK}__rp-cell`} style={{ width: "36%" }} />
                  <span className={`${BLOCK}__rp-cell`} style={{ width: "26%" }} />
                  <span className={`${BLOCK}__rp-cell`} style={{ width: "20%" }} />
                </div>
              </div>

              <div className={`${BLOCK}__rp-qr`}>
                <span className={`${BLOCK}__rp-handle ${BLOCK}__rp-handle--tl`} />
                <span className={`${BLOCK}__rp-handle ${BLOCK}__rp-handle--tr`} />
                <span className={`${BLOCK}__rp-handle ${BLOCK}__rp-handle--bl`} />
                <span className={`${BLOCK}__rp-handle ${BLOCK}__rp-handle--br`} />
                <svg width="100%" height="100%" viewBox="0 0 24 24" aria-hidden="true">
                  <path
                    fill="#26241f"
                    d="M2 2h7v7H2V2Zm2 2v3h3V4H4Zm11-2h7v7h-7V2Zm2 2v3h3V4h-3ZM2 15h7v7H2v-7Zm2 2v3h3v-3H4Zm9-2h2v2h-2v-2Zm4 0h2v2h-2v-2Zm2 2h2v2h-2v-2Zm-6 2h2v2h-2v-2Zm4 0h2v2h-2v-2Zm2 2h2v2h-2v-2Zm-6 0h2v2h-2v-2ZM11 2h2v5h-2V2Zm0 7h2v2h-2V9ZM2 11h5v2H2v-2Zm7 0h2v2H9v-2Z"
                  />
                </svg>
              </div>
            </div>

            {/* A real report token, not invented syntax. */}
            <span className={`${BLOCK}__rp-token`}>
              Page {"{CURRENT_PAGE}"} of {"{TOTAL_PAGES}"}
            </span>
          </div>
        </div>
      </div>

      <span className={`${BLOCK}__chip ${BLOCK}__rp-size`}>A4: 210 x 297 millimeters</span>
      <p className={`${BLOCK}__rp-hint`}>Design on a real page — the PDF your users download is what you see here.</p>
    </BannerMockFrame>
  );
};

export const ScheduledJobsIllustration: React.FC = () => {
  return (
    <BannerMockFrame tool="scheduled-jobs">
      <div className={`${BLOCK}__sj-body`}>
        <div className={`${BLOCK}__sj-head`}>
          <span className={`${BLOCK}__sj-icon`}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
              <path
                d="M12 7V12L15.5 14"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          <span className={`${BLOCK}__sj-title`}>Scheduled Jobs</span>
          <span className={`${BLOCK}__sj-count`}>3 jobs</span>
        </div>

        <div className={`${BLOCK}__sj-table`}>
          <div className={`${BLOCK}__sj-row ${BLOCK}__sj-row--head`}>
            <span className={`${BLOCK}__sj-cell-name ${BLOCK}__sj-head-label`}>Name</span>
            <span className={`${BLOCK}__sj-cell-schedule ${BLOCK}__sj-head-label`}>Schedule</span>
            <span className={`${BLOCK}__sj-cell-status ${BLOCK}__sj-head-label`}>Status</span>
          </div>

          <div className={`${BLOCK}__sj-row`}>
            <span className={`${BLOCK}__sj-cell-name ${BLOCK}__sj-name`}>Nightly sync</span>
            <span className={`${BLOCK}__sj-cell-schedule`}>
              {/* The sentence says what it does; the expression is what you copy. */}
              <span className={`${BLOCK}__sj-schedule`}>
                <span className={`${BLOCK}__sj-schedule-text`}>At 12:00 AM, every day</span>
                <span className={`${BLOCK}__sj-schedule-cron`}>0 0 0 ? * * *</span>
              </span>
            </span>
            <span className={`${BLOCK}__sj-cell-status`}>
              <span className={`${BLOCK}__sj-pill ${BLOCK}__sj-pill--active`}>
                <span className={`${BLOCK}__sj-pill-dot`} />
                Active
              </span>
            </span>
          </div>

          <div className={`${BLOCK}__sj-row`}>
            <span className={`${BLOCK}__sj-cell-name ${BLOCK}__sj-name`}>Hourly import</span>
            <span className={`${BLOCK}__sj-cell-schedule`}>
              <span className={`${BLOCK}__sj-schedule`}>
                <span className={`${BLOCK}__sj-schedule-text`}>At 0 minutes past the hour</span>
                <span className={`${BLOCK}__sj-schedule-cron`}>0 0 * ? * * *</span>
              </span>
            </span>
            <span className={`${BLOCK}__sj-cell-status`}>
              <span className={`${BLOCK}__sj-pill ${BLOCK}__sj-pill--active`}>
                <span className={`${BLOCK}__sj-pill-dot`} />
                Active
              </span>
            </span>
          </div>

          <div className={`${BLOCK}__sj-row`}>
            <span className={`${BLOCK}__sj-cell-name ${BLOCK}__sj-name`}>Weekly report</span>
            <span className={`${BLOCK}__sj-cell-schedule`}>
              <span className={`${BLOCK}__sj-schedule`}>
                <span className={`${BLOCK}__sj-schedule-text`}>At 08:00 AM, only on Monday</span>
                <span className={`${BLOCK}__sj-schedule-cron`}>0 0 8 ? * MON *</span>
              </span>
            </span>
            <span className={`${BLOCK}__sj-cell-status`}>
              <span className={`${BLOCK}__sj-pill ${BLOCK}__sj-pill--idle`}>
                <span className={`${BLOCK}__sj-pill-dot`} />
                Inactive
              </span>
            </span>
          </div>
        </div>
      </div>
    </BannerMockFrame>
  );
};

export const ConfigManagerIllustration: React.FC = () => {
  return (
    <BannerMockFrame tool="config-manager">
      <div className={`${BLOCK}__cm-body`}>
        <div className={`${BLOCK}__cm-tabs`}>
          <span className={`${BLOCK}__cm-tab`}>App Settings</span>
          <span className={`${BLOCK}__cm-tab ${BLOCK}__cm-tab--on`}>Web</span>
          <span className={`${BLOCK}__cm-tab`}>IOS</span>
          <span className={`${BLOCK}__cm-tab`}>Android</span>
          <span className={`${BLOCK}__cm-tab`}>Desktop App</span>
        </div>

        {/* Every pane opens with this strip; off hides the whole body below it. */}
        <div className={`${BLOCK}__cm-support`}>
          <span className={`${BLOCK}__cm-switch`}>
            <span className={`${BLOCK}__cm-knob`} />
          </span>
          <span className={`${BLOCK}__cm-support-text`}>
            <span className={`${BLOCK}__cm-support-label`}>Support Web UI</span>
            <span className={`${BLOCK}__cm-support-note`}>
              Turn this off and the web build is left out of this configuration.
            </span>
          </span>
        </div>

        <div className={`${BLOCK}__cm-section`}>
          <span className={`${BLOCK}__cm-section-title`}>HOST SETTINGS</span>

          <div className={`${BLOCK}__cm-field`}>
            <span className={`${BLOCK}__cm-field-label`}>Base URL</span>
            <span className={`${BLOCK}__cm-field-input`}>https://app.example.com</span>
          </div>

          <div className={`${BLOCK}__cm-field`}>
            <span className={`${BLOCK}__cm-field-label`}>Session timeout</span>
            <span className={`${BLOCK}__cm-field-input ${BLOCK}__cm-field-input--muted`}>30 minutes</span>
          </div>
        </div>

        <div className={`${BLOCK}__cm-foot`}>
          <span className={`${BLOCK}__cm-foot-dot`} />
          <span>One configuration per environment — test, staging, production.</span>
        </div>
      </div>
    </BannerMockFrame>
  );
};
